<script>
  import { onMount, onDestroy } from 'svelte';
  import StatusBadge from '../components/StatusBadge.svelte';
  import { getSystemStatus } from '../services/api';

  let data = null;
  let loading = true;
  let interval;

  onMount(async () => {
    await refresh();
    interval = setInterval(refresh, 5000);
  });

  onDestroy(() => {
    if (interval) clearInterval(interval);
  });

  async function refresh() {
    try {
      const result = await getSystemStatus();
      if (result.success) {
        data = result;
      }
    } catch (err) {
      console.error('Dashboard refresh error:', err);
    } finally {
      loading = false;
    }
  }

  function formatTime(ts) {
    const d = new Date(ts);
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' });
  }
</script>

<div class="dashboard-page">
  <h1 class="section-title">
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
      <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
    </svg>
    Dashboard
  </h1>

  {#if loading}
    <div class="loading-state">
      <div class="spinner"></div>
      <span>Loading system status...</span>
    </div>
  {:else if data}
    <!-- System Status -->
    <div class="dashboard-grid">
      <div class="status-card-wrap" id="system-status-card">
        <StatusBadge
          level={data.system.level}
          message={data.system.message}
        />
        <div class="status-counts">
          <div class="count-item">
            <span class="count-value">{data.system.activeApps}</span>
            <span class="count-sep">/</span>
            <span class="count-total">{data.system.totalApps}</span>
            <span class="count-label">Active Apps</span>
          </div>
          <div class="count-divider"></div>
          <div class="count-item">
            <span class="count-value" class:has-issues={data.system.issues > 0}>{data.system.issues}</span>
            <span class="count-label">Issues</span>
          </div>
        </div>
      </div>

      <!-- Quick Stats -->
      <div class="card quick-stats" id="quick-stats-card">
        <div class="card-title">Quick Stats</div>
        <div class="stats-grid">
          <div class="stat-item">
            <div class="stat-icon cpu-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/>
              </svg>
            </div>
            <div class="stat-data">
              <span class="stat-number">{data.system.quickStats.cpu}%</span>
              <span class="stat-name">CPU</span>
            </div>
          </div>
          <div class="stat-item">
            <div class="stat-icon mem-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <rect x="2" y="6" width="20" height="12" rx="2"/>
              </svg>
            </div>
            <div class="stat-data">
              <span class="stat-number">{data.system.quickStats.memory}%</span>
              <span class="stat-name">Memory</span>
            </div>
          </div>
          <div class="stat-item">
            <div class="stat-icon uptime-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
            </div>
            <div class="stat-data">
              <span class="stat-number">{data.system.uptime.formatted}</span>
              <span class="stat-name">Uptime</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Safe Mode Status -->
      {#if data.system.safeMode.active}
        <div class="card safe-mode-card">
          <div class="card-title">⚠️ Safe Mode</div>
          <p class="safe-mode-text">Active — Non-critical apps disabled</p>
          <div class="safe-mode-stats">
            CPU: {data.system.safeMode.cpu}% | Memory: {data.system.safeMode.memory}%
          </div>
        </div>
      {/if}

      <!-- Recent Activity -->
      <div class="card activity-card" id="recent-activity-card">
        <div class="card-title">Recent Activity</div>
        {#if data.activity && data.activity.length > 0}
          <div class="activity-list">
            {#each data.activity.slice(0, 8) as item}
              <div class="activity-row">
                <span class="activity-dot"></span>
                <span class="activity-text">{item.message}</span>
                <span class="activity-time">{formatTime(item.timestamp)}</span>
              </div>
            {/each}
          </div>
        {:else}
          <div class="no-activity">No recent activity</div>
        {/if}
      </div>
    </div>
  {/if}
</div>

<style>
  .dashboard-page {
    animation: fadeIn 0.3s ease;
  }

  .loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    padding: 60px;
    color: var(--text-secondary);
    font-size: 14px;
  }

  .spinner {
    width: 28px;
    height: 28px;
    border: 2px solid var(--border-default);
    border-top-color: var(--neon-blue);
    border-radius: 50%;
    animation: spinSlow 0.8s linear infinite;
  }

  .dashboard-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
  }

  .status-card-wrap {
    display: flex;
    flex-direction: column;
    gap: 16px;
    animation: fadeInUp 0.4s ease both;
  }

  .status-counts {
    display: flex;
    align-items: center;
    gap: 24px;
    padding: 16px 20px;
    background: rgba(255, 255, 255, 0.02);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-md);
  }

  .count-item {
    display: flex;
    align-items: baseline;
    gap: 4px;
  }

  .count-value {
    font-size: 28px;
    font-weight: 800;
    color: var(--neon-green);
    line-height: 1;
  }

  .count-value.has-issues {
    color: var(--neon-red);
  }

  .count-sep {
    font-size: 18px;
    color: var(--text-tertiary);
    font-weight: 300;
  }

  .count-total {
    font-size: 18px;
    color: var(--text-secondary);
    font-weight: 600;
  }

  .count-label {
    font-size: 12px;
    color: var(--text-secondary);
    margin-left: 6px;
  }

  .count-divider {
    width: 1px;
    height: 32px;
    background: var(--border-default);
  }

  .quick-stats {
    animation: fadeInUp 0.4s ease 0.1s both;
  }

  .stats-grid {
    display: flex;
    gap: 20px;
  }

  .stat-item {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px;
    background: rgba(255, 255, 255, 0.02);
    border-radius: var(--radius-sm);
    border: 1px solid var(--border-default);
  }

  .stat-icon {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--radius-sm);
    flex-shrink: 0;
  }

  .cpu-icon { background: rgba(6, 182, 212, 0.1); color: var(--neon-cyan); }
  .mem-icon { background: rgba(139, 92, 246, 0.1); color: var(--neon-purple); }
  .uptime-icon { background: rgba(16, 185, 129, 0.1); color: var(--neon-green); }

  .stat-data {
    display: flex;
    flex-direction: column;
  }

  .stat-number {
    font-size: 18px;
    font-weight: 700;
    color: var(--text-primary);
    line-height: 1.2;
  }

  .stat-name {
    font-size: 11px;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .safe-mode-card {
    grid-column: 1 / -1;
    background: rgba(245, 158, 11, 0.06);
    border-color: rgba(245, 158, 11, 0.2);
  }

  .safe-mode-text {
    color: var(--neon-orange);
    font-size: 14px;
    font-weight: 600;
  }

  .safe-mode-stats {
    margin-top: 8px;
    color: var(--text-secondary);
    font-size: 13px;
  }

  .activity-card {
    grid-column: 1 / -1;
    animation: fadeInUp 0.4s ease 0.2s both;
  }

  .activity-list {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .activity-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 8px;
    border-radius: var(--radius-sm);
    transition: background 0.2s ease;
  }

  .activity-row:hover {
    background: rgba(255, 255, 255, 0.02);
  }

  .activity-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--neon-blue);
    flex-shrink: 0;
    opacity: 0.6;
  }

  .activity-text {
    flex: 1;
    font-size: 13px;
    color: var(--text-secondary);
  }

  .activity-time {
    font-size: 12px;
    color: var(--text-tertiary);
    flex-shrink: 0;
  }

  .no-activity {
    padding: 20px;
    text-align: center;
    color: var(--text-tertiary);
    font-size: 13px;
  }

  @media (max-width: 768px) {
    .dashboard-grid { grid-template-columns: 1fr; }
    .stats-grid { flex-direction: column; gap: 10px; }
    .status-counts { gap: 16px; }
  }
</style>
